import { useState } from 'react';
import { CompanyLinkContactCardType, CompanyLinkType } from '@api';
import { useDisclosure } from '@mantine/hooks';
import { ContactCardActionModeType, getContactCardActionMode } from './helpers';
import {
  useCreateContactCard,
  useRegenerateContactCard,
  useWithdrawContactCard,
} from './hooks';

type UseContactCardArgsType = {
  personId: string;
  companyLink: CompanyLinkType;
};

type UseContactCardReturnType = {
  contactCardActionMode: ContactCardActionModeType;
  onContactCardClick: () => Promise<void>;
  isContactCardActionDisabled: boolean;
  contactCardModal: {
    opened: boolean;
    onClose: () => void;
    onWithdraw: () => void;
    onRegenerate: () => void;
    contactCard: CompanyLinkContactCardType;
  } | null;
};

export const useContactCard = ({
  personId,
  companyLink,
}: UseContactCardArgsType): UseContactCardReturnType => {
  const { createContactCard, isCreateContactCardPending } =
    useCreateContactCard({ personId });
  const [opened, { open, close }] = useDisclosure(false);
  const [contactCard, setContactCard] =
    useState<CompanyLinkContactCardType | null>(null);

  const closeContactCardModal = (): void => {
    close();
    setContactCard(null);
  };

  const { onWithdraw: withdrawContactCard } = useWithdrawContactCard({
    personId,
    onClose: closeContactCardModal,
  });
  const { onRegenerate: regenerateContactCard } = useRegenerateContactCard({
    personId,
    onSuccess: setContactCard,
  });

  const contactCardActionMode = getContactCardActionMode(companyLink);

  const onContactCardClick = async () => {
    if (contactCardActionMode === 'open' && companyLink.contactCard) {
      setContactCard(companyLink.contactCard);
      open();
      return;
    }

    if (contactCardActionMode === 'create') {
      const created = await createContactCard(companyLink.linkId);
      if (!created) {
        return;
      }
      setContactCard(created);
      open();
    }
  };

  const contactCardModal = contactCard
    ? {
        opened,
        onClose: close,
        onWithdraw: () => withdrawContactCard(companyLink.linkId),
        onRegenerate: () => regenerateContactCard(companyLink.linkId),
        contactCard,
      }
    : null;

  return {
    contactCardActionMode,
    onContactCardClick,
    isContactCardActionDisabled: isCreateContactCardPending,
    contactCardModal,
  };
};
